<script setup lang="ts">
import { watch } from 'vue';
import { ZPageButton, ZScalable } from '@zoomcatalog/design-system';
import {
  ZProductInfoSidebar,
  ZSkeletonTemplate,
} from '@zoomcatalog/design-templates';
import ActionBar from '@/components/action-panel/ActionBar.vue';
import DesignPage from '@/components/design-viewer/DesignPage.vue';
import ViewerLayout from '@/components/design-viewer/ViewerLayout.vue';

import {
  usePaginationInstance,
  usePaginationHandlers,
} from '@/composable/usePagination';
import { useTrackingHandlers } from '@/composable/useTracking';
import type { IDesign } from '@/service/ts-definitions';

defineProps<{
  design: IDesign | undefined;
}>();

const pagination = usePaginationInstance();
const paginationHandlers = usePaginationHandlers();
const trackingHandlers = useTrackingHandlers();
watch(
  () => pagination.currentPages,
  (newCurrentPages) => {
    if (!newCurrentPages.length) return;
    trackingHandlers.pageViewDebouncer(newCurrentPages);
    trackingHandlers.timeOnPageThrottler();
  },
  { immediate: true }
);
</script>

<template>
  <ViewerLayout>
    <template #toolbar>
      <ActionBar />
    </template>
    <template #default>
      <template v-if="pagination.totalPages">
        <ZScalable centerScale>
          <template #default>
            <template v-if="pagination.screenType === 'desktop'">
              <template v-if="pagination.currentPages.length">
                <template
                  v-for="page in pagination.currentPages"
                  :key="page.pageId"
                >
                  <DesignPage v-if="page" :page="page" useAutoScaling />
                </template>
              </template>
              <template v-else>
                <ZSkeletonTemplate
                  v-for="basicPage in pagination.currentBasicPages"
                  :key="basicPage.page_id"
                />
              </template>
            </template>
            <template v-else>
              <DesignPage
                v-if="pagination.currentPages[0]"
                :page="pagination.currentPages[0]"
                useAutoScaling
              />
              <ZSkeletonTemplate v-else />
            </template>
          </template>

          <template #footer>
            <div class="disclaimer">
              Product prices and availability may change. Please confirm before
              ordering.
            </div>
          </template>
        </ZScalable>
      </template>
    </template>

    <template #navigation-left>
      <ZPageButton
        :size="pagination.screenType === 'mobile' ? 'sm' : 'md'"
        direction="left"
        :disabled="!pagination.canGoPrevious"
        @click="paginationHandlers.goToPreviousPage"
      />
    </template>

    <template #navigation-right>
      <ZPageButton
        :size="pagination.screenType === 'mobile' ? 'sm' : 'md'"
        direction="right"
        :disabled="!pagination.canGoNext"
        @click="paginationHandlers.goToNextPage()"
      />
    </template>
  </ViewerLayout>

  <ZProductInfoSidebar />
</template>

<style lang="scss" scoped>
.disclaimer {
  @apply text-center text-longform-xs text-secondary;
  @apply py-2;
}
</style>
